import cornerstone from 'cornerstone-core';
import dicomParser from 'dicom-parser';

const CODEC_BASE_URL = `${process.env.PUBLIC_URL || ''}/codecs/`;
const openjpegGlueUrl = `${CODEC_BASE_URL}openjpegwasm_decode.js`;
const openjpegWasmUrl = `${CODEC_BASE_URL}openjpegwasm_decode.wasm`;

export const JPEG_2000_TRANSFER_SYNTAXES = new Set([
    '1.2.840.10008.1.2.4.90',
    '1.2.840.10008.1.2.4.91',
]);

const SCHEME = 'j2kfile';
const DECODED_IMAGE_CACHE_LIMIT = 6;

const registeredFiles = new Map();
const decodedImageCache = new Map();
let nextFileIndex = 0;
let codecPromise = null;
let schemeRegistered = false;

const loadGlueScript = () => new Promise((resolve, reject) => {
    if (typeof window.OpenJPEGWASM === 'function') {
        resolve(window.OpenJPEGWASM);
        return;
    }
    const script = document.createElement('script');
    script.src = openjpegGlueUrl;
    script.async = true;
    script.onload = () => {
        if (typeof window.OpenJPEGWASM === 'function') {
            resolve(window.OpenJPEGWASM);
        } else {
            reject(new Error('OpenJPEG glue loaded but did not define OpenJPEGWASM'));
        }
    };
    script.onerror = () => reject(new Error(`Failed to load OpenJPEG glue from ${script.src}`));
    document.head.appendChild(script);
});

const getCodec = () => {
    if (!codecPromise) {
        codecPromise = loadGlueScript()
            .then((OpenJPEGWASM) => OpenJPEGWASM({
                locateFile: (path) => (path.endsWith('.wasm') ? openjpegWasmUrl : path),
                print: () => {},
            }))
            .catch((error) => {
                codecPromise = null;
                throw error;
            });
    }
    return codecPromise;
};

const parseImageId = (imageId) => {
    const [baseImageId, query = ''] = imageId.split('?');
    const frameMatch = query.match(/(?:^|&)frame=(\d+)/);
    return {
        baseImageId,
        frameIndex: frameMatch ? parseInt(frameMatch[1], 10) : 0,
    };
};

const readFrameCodestream = (dataSet, frameIndex) => {
    const pixelDataElement = dataSet.elements.x7fe00010;
    if (!pixelDataElement || !pixelDataElement.encapsulatedPixelData) {
        throw new Error('JPEG 2000 pixel data is not encapsulated');
    }
    const numberOfFrames = parseInt(dataSet.intString('x00280008'), 10) || 1;
    const { basicOffsetTable, fragments } = pixelDataElement;

    if (basicOffsetTable && basicOffsetTable.length > 0) {
        return dicomParser.readEncapsulatedImageFrame(dataSet, pixelDataElement, frameIndex);
    }
    if (numberOfFrames === 1) {
        return dicomParser.readEncapsulatedPixelDataFromFragments(
            dataSet, pixelDataElement, 0, fragments.length,
        );
    }
    if (fragments.length === numberOfFrames) {
        return dicomParser.readEncapsulatedPixelDataFromFragments(
            dataSet, pixelDataElement, frameIndex, 1,
        );
    }
    throw new Error('Cannot locate frame boundaries in multi-fragment JPEG 2000 pixel data');
};

const decodeCodestream = async (codestream) => {
    const codec = await getCodec();
    const decoder = new codec.J2KDecoder();
    try {
        const encodedBuffer = decoder.getEncodedBuffer(codestream.length);
        encodedBuffer.set(codestream);
        decoder.decode();
        const frameInfo = decoder.getFrameInfo();
        const decodedBuffer = new Uint8Array(decoder.getDecodedBuffer());
        return {
            width: frameInfo.width,
            height: frameInfo.height,
            bitsPerSample: frameInfo.bitsPerSample,
            componentCount: frameInfo.componentCount,
            isSigned: frameInfo.isSigned,
            bytes: decodedBuffer,
        };
    } finally {
        decoder.delete();
    }
};

const ybrFullToRgb = (bytes, pixelCount) => {
    for (let i = 0; i < pixelCount; i += 1) {
        const offset = i * 3;
        const y = bytes[offset];
        const cb = bytes[offset + 1] - 128;
        const cr = bytes[offset + 2] - 128;
        bytes[offset] = Math.max(0, Math.min(255, Math.round(y + 1.402 * cr)));
        bytes[offset + 1] = Math.max(0, Math.min(255, Math.round(y - 0.344136 * cb - 0.714136 * cr)));
        bytes[offset + 2] = Math.max(0, Math.min(255, Math.round(y + 1.772 * cb)));
    }
};

const buildColorPixelData = (decoded, photometricInterpretation) => {
    const pixelCount = decoded.width * decoded.height;
    if (decoded.componentCount !== 3 || decoded.bitsPerSample > 8) {
        throw new Error(
            `Unsupported JPEG 2000 color layout: ${decoded.componentCount} components, ${decoded.bitsPerSample} bits`,
        );
    }
    if (photometricInterpretation === 'YBR_FULL' || photometricInterpretation === 'YBR_FULL_422') {
        ybrFullToRgb(decoded.bytes, pixelCount);
    }
    const rgba = new Uint8Array(pixelCount * 4);
    for (let i = 0; i < pixelCount; i += 1) {
        const source = i * 3;
        const target = i * 4;
        rgba[target] = decoded.bytes[source];
        rgba[target + 1] = decoded.bytes[source + 1];
        rgba[target + 2] = decoded.bytes[source + 2];
        rgba[target + 3] = 255;
    }
    return rgba;
};

const buildGrayscalePixelData = (decoded) => {
    const pixelCount = decoded.width * decoded.height;
    const copy = decoded.bytes.slice().buffer;
    if (decoded.bitsPerSample <= 8) {
        return decoded.isSigned ? new Int8Array(copy, 0, pixelCount) : new Uint8Array(copy, 0, pixelCount);
    }
    return decoded.isSigned ? new Int16Array(copy, 0, pixelCount) : new Uint16Array(copy, 0, pixelCount);
};

const computeMinMax = (pixelData) => {
    let min = pixelData[0];
    let max = pixelData[0];
    for (let i = 1; i < pixelData.length; i += 1) {
        const value = pixelData[i];
        if (value < min) min = value;
        if (value > max) max = value;
    }
    return { min, max };
};

const firstNumber = (dataSet, tag, fallback) => {
    const value = dataSet.floatString(tag, 0);
    return Number.isFinite(value) ? value : fallback;
};

const readPixelSpacing = (dataSet) => {
    const spacing = dataSet.string('x00280030');
    if (!spacing) return { rowPixelSpacing: undefined, columnPixelSpacing: undefined };
    const [row, column] = spacing.split('\\').map(parseFloat);
    return {
        rowPixelSpacing: Number.isFinite(row) ? row : undefined,
        columnPixelSpacing: Number.isFinite(column) ? column : undefined,
    };
};

const createCornerstoneImage = (imageId, dataSet, decoded) => {
    const photometricInterpretation = dataSet.string('x00280004') || '';
    const isColor = decoded.componentCount === 3;
    const spacing = readPixelSpacing(dataSet);

    const base = {
        imageId,
        rows: decoded.height,
        columns: decoded.width,
        height: decoded.height,
        width: decoded.width,
        rowPixelSpacing: spacing.rowPixelSpacing,
        columnPixelSpacing: spacing.columnPixelSpacing,
        invert: false,
        render: undefined,
        data: dataSet,
    };

    if (isColor) {
        const pixelData = buildColorPixelData(decoded, photometricInterpretation);
        return {
            ...base,
            color: true,
            rgba: true,
            slope: 1,
            intercept: 0,
            minPixelValue: 0,
            maxPixelValue: 255,
            windowCenter: 128,
            windowWidth: 256,
            sizeInBytes: pixelData.byteLength,
            getPixelData: () => pixelData,
        };
    }

    const pixelData = buildGrayscalePixelData(decoded);
    const { min, max } = computeMinMax(pixelData);
    const slope = firstNumber(dataSet, 'x00281053', 1);
    const intercept = firstNumber(dataSet, 'x00281052', 0);
    const windowCenter = firstNumber(dataSet, 'x00281050', ((max + min) / 2) * slope + intercept);
    const windowWidth = firstNumber(dataSet, 'x00281051', Math.max(1, (max - min) * slope));
    return {
        ...base,
        color: false,
        rgba: false,
        slope,
        intercept,
        minPixelValue: min,
        maxPixelValue: max,
        windowCenter,
        windowWidth,
        invert: photometricInterpretation === 'MONOCHROME1',
        sizeInBytes: pixelData.byteLength,
        getPixelData: () => pixelData,
    };
};

const rememberDecodedImage = (imageId, promise) => {
    decodedImageCache.delete(imageId);
    decodedImageCache.set(imageId, promise);
    while (decodedImageCache.size > DECODED_IMAGE_CACHE_LIMIT) {
        const oldestKey = decodedImageCache.keys().next().value;
        decodedImageCache.delete(oldestKey);
    }
};

const loadJpeg2000Image = (imageId) => {
    const cached = decodedImageCache.get(imageId);
    if (cached) {
        rememberDecodedImage(imageId, cached);
        return { promise: cached, cancelFn: undefined };
    }

    const { baseImageId, frameIndex } = parseImageId(imageId);
    const entry = registeredFiles.get(baseImageId);
    const promise = (async () => {
        if (!entry) {
            throw new Error(`${SCHEME}: unknown imageId ${imageId}`);
        }
        const start = performance.now();
        const codestream = readFrameCodestream(entry.dataSet, frameIndex);
        const decoded = await decodeCodestream(codestream);
        const image = createCornerstoneImage(imageId, entry.dataSet, decoded);
        image.decodeTimeInMS = performance.now() - start;
        return image;
    })();

    rememberDecodedImage(imageId, promise);
    promise.catch(() => decodedImageCache.delete(imageId));
    return { promise, cancelFn: undefined };
};

const ensureSchemeRegistered = () => {
    if (schemeRegistered) return;
    cornerstone.registerImageLoader(SCHEME, loadJpeg2000Image);
    schemeRegistered = true;
};

export const jpeg2000ImageLoader = {
    isJpeg2000DataSet: (dataSet) => JPEG_2000_TRANSFER_SYNTAXES.has(dataSet.string('x00020010')),
    addFile: (file, dataSet) => {
        ensureSchemeRegistered();
        const imageId = `${SCHEME}:${nextFileIndex}`;
        nextFileIndex += 1;
        registeredFiles.set(imageId, { file, dataSet });
        return imageId;
    },
};
